"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";

type Item = { name: string; price: number; guest: number };

const TABLE: Item[] = [
  { name: "Caesar Salad", price: 14, guest: 1 },
  { name: "Burrata Toast", price: 16, guest: 2 },
  { name: "Calamari", price: 18, guest: 3 },
  { name: "Branzino", price: 38, guest: 1 },
  { name: "Ribeye 14oz", price: 38, guest: 2 },
  { name: "Linguine Vongole", price: 26, guest: 3 },
  { name: "Half Roast Chicken", price: 28, guest: 4 },
  { name: "House Cab", price: 14, guest: 1 },
  { name: "Negroni", price: 16, guest: 2 },
  { name: "Sparkling Water", price: 6, guest: 4 },
];

const GUEST_COLORS = [
  { ring: "ring-redy-orange", tint: "bg-redy-orange/10", chip: "bg-redy-orange text-white" },
  { ring: "ring-redy-red", tint: "bg-redy-red/10", chip: "bg-redy-red text-white" },
  { ring: "ring-redy-blue", tint: "bg-redy-blue/10", chip: "bg-redy-blue text-white" },
  { ring: "ring-redy-charcoal", tint: "bg-redy-charcoal/5", chip: "bg-redy-charcoal text-white" },
];

const TIPS = [22, 20, 18, 25];

export default function RestaurantsSplitChecks() {
  const { t } = useLang();

  const subtotal = TABLE.reduce((a, b) => a + b.price, 0);
  const groups = [1, 2, 3, 4].map((g) => TABLE.filter((it) => it.guest === g));

  return (
    <section className="py-24 px-6 bg-redy-charcoal text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-redy-orange/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-redy-red/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1140px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("Bill splits", "Splits de cuenta")}
          </div>
          <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight mb-4">
            {t("One table.", "Una mesa.")}
            <br />
            <span className="redy-text-gradient">{t("Four checks.", "Cuatro cuentas.")}</span>
          </h2>
          <p className="text-lg text-white/70 max-w-[580px] mx-auto font-medium">
            {t(
              "Drag any item to any guest. Tips, taxes, and discounts split with the right math automatically. The host pays for the round; everyone else pays their own.",
              "Arrastra cualquier ítem a cualquier invitado. Tips, impuestos y descuentos se dividen con la matemática correcta. El anfitrión paga la ronda; cada quien paga lo suyo."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-[0.95fr_1.4fr] gap-6 items-start">
          {/* Single check before split */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="bg-white text-redy-charcoal rounded-3xl p-6 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)]"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-redy-charcoal/50 mb-3">
              {t("Table 12 · One check", "Mesa 12 · Una cuenta")}
            </div>
            <div className="space-y-1.5 max-h-[300px] overflow-hidden">
              {TABLE.map((it, i) => {
                const g = GUEST_COLORS[it.guest - 1];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -6 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className="flex items-center justify-between text-sm py-1 border-b border-black/[0.04]"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-5 h-5 rounded-full ${g.chip} text-[10px] font-black flex items-center justify-center`}
                      >
                        {it.guest}
                      </span>
                      <span className="font-medium">{it.name}</span>
                    </div>
                    <span className="font-mono tabular-nums">${it.price}</span>
                  </motion.div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t border-black/10 flex items-center justify-between font-bold">
              <span className="text-sm">{t("Subtotal", "Subtotal")}</span>
              <span className="font-mono tabular-nums text-lg">${subtotal}</span>
            </div>
          </motion.div>

          {/* 4 split checks */}
          <div className="grid grid-cols-2 gap-3">
            {groups.map((items, gi) => {
              const sub = items.reduce((a, b) => a + b.price, 0);
              const tipPct = TIPS[gi];
              const tip = +((sub * tipPct) / 100).toFixed(2);
              const total = +(sub + tip).toFixed(2);
              const c = GUEST_COLORS[gi];
              return (
                <motion.div
                  key={gi}
                  initial={{ opacity: 0, scale: 0.9, y: 12 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.5,
                    delay: 0.2 + gi * 0.12,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className={`bg-white text-redy-charcoal rounded-2xl p-5 ring-2 ${c.ring} ring-offset-2 ring-offset-redy-charcoal shadow-[0_10px_30px_-15px_rgba(0,0,0,0.6)]`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-7 h-7 rounded-full ${c.chip} text-xs font-black flex items-center justify-center`}
                      >
                        {gi + 1}
                      </span>
                      <span className="text-sm font-black">
                        {t(`Guest ${gi + 1}`, `Invitado ${gi + 1}`)}
                      </span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50">
                      {items.length} {t("items", "ítems")}
                    </span>
                  </div>
                  <div className="space-y-1 mb-3">
                    {items.map((it, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-redy-charcoal/70 truncate pr-2">{it.name}</span>
                        <span className="font-mono tabular-nums font-medium flex-shrink-0">
                          ${it.price}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-black/5 pt-3 space-y-1 text-xs">
                    <div className="flex justify-between text-redy-charcoal/60">
                      <span>{t("Subtotal", "Subtotal")}</span>
                      <span className="font-mono tabular-nums">${sub}</span>
                    </div>
                    <div className="flex justify-between text-redy-orange font-bold">
                      <span>
                        {t("Tip", "Propina")} ({tipPct}%)
                      </span>
                      <span className="font-mono tabular-nums">+${tip.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-base font-black pt-1">
                      <span>{t("Total", "Total")}</span>
                      <span className="font-mono tabular-nums">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 inline-flex w-full flex-col md:flex-row items-center justify-between gap-4 bg-white/[0.04] border border-white/10 rounded-2xl px-6 py-5"
        >
          <div className="flex items-center gap-6 text-sm">
            <Stat value={t("Item", "Ítem")} label={t("or guest, you pick", "o invitado, tú eliges")} />
            <Stat value="∞" label={t("ways to split", "formas de split")} />
            <Stat value={t("Covered", "Cubiertos")} label={t("card fees, with dual pricing", "fees de tarjeta, con dual pricing")} />
          </div>
          <span className="text-xs text-white/50 italic">
            {t(
              "Items can be dragged; tips per person; comp items, voids and discounts all split right.",
              "Los ítems se arrastran; tip por persona; comps, voids y descuentos se dividen bien."
            )}
          </span>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="font-mono tabular-nums text-2xl font-black text-redy-orange">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-white/50 mt-1">
        {label}
      </div>
    </div>
  );
}
